import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Card,
  CardContent,
  Chip,
  ChipProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  SvgIconComponent,
  EventAvailable as EventAvailableIcon,
  AccessTime as AccessTimeIcon,
  Timelapse as TimelapseIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import moment, { Moment } from 'moment';

import { getScheduleByIdAsUser } from '../../../../store/V2/ScheduleSlice/api';
import {
  getScheduleState,
  IScheduleFullEvent,
  removeUserScheduleEventFromList,
  resetUserScheduleEvent,
  SCHEDULE_TYPES,
  updateOneUserScheduleItem,
  updateScheduleEvent,
} from '../../../../store/V2/ScheduleSlice';
import {
  IZoomMeetingRequest,
  ZOOM_MEETING_REQUEST_STATUSES,
  ZOOM_MEETING_REQUEST_TYPES,
} from '../../../../store/V2/ZoomRequestsSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  IDENTIFIED_USER_ROLES,
  TIdentifiedUserRole,
  USER_PERMISSION_INDIVIDUALS,
} from '../../../../store/UserSlice';
import { useAppSelector } from '../../../../hooks';
import {
  checkUserPermission,
  getClassShortUrlByWaitingId,
  getSubjectColorNew,
  getSubjectIconName,
} from '../../../../helper';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import ClassCancellationModal from '../../ViewCourse/modals/ClassCancellationModal';
import ClassRescheduleModal from '../../ViewCourse/modals/ClassRescheduleModal';
import { ISectionForUser } from '../../../../store/V2/SectionSlice';
import ShortUserCard from '../../../../components/V2/Cards/ShortUserCard';

interface ViewScheduleModalProps {
  open: boolean;
  onClose: () => void;
  sectionId: string;
}

const DetailItem = ({
  icon,
  title,
}: {
  icon: SvgIconComponent;
  title: string;
}) => {
  return (
    <Grid container columnSpacing="13px">
      <Grid item>
        <Icon
          component={icon}
          sx={{ width: '24px', height: '24px', color: '#000000' }}
        />
      </Grid>
      <Grid item xs>
        <Stack>
          <Typography fontSize="16px" fontWeight={500}>
            {title}
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
};

const TextDetailItem = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  return (
    <Grid container columnGap="10px">
      <Grid item>
        <Typography fontSize="16px" fontWeight={500} color="#1E293B">
          {title} :
        </Typography>
      </Grid>
      <Grid item xs>
        <Typography fontSize="16px" fontWeight={500} color="#94A3B8">
          {description}
        </Typography>
      </Grid>
    </Grid>
  );
};

const SubjectCard = ({
  subject,
  subjectIconName,
  subjectColor,
}: {
  subject: string;
  subjectIconName: string;
  subjectColor: string;
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '10px',
        background: `url('${process.env.PUBLIC_URL}/images/subjectPage/shape-header.png') top right/contain no-repeat, url('${process.env.PUBLIC_URL}/images/landingPage/icon-${subjectIconName}-white.avif') right center/contain no-repeat, ${subjectColor}`,
        color: '#ffffff',
        mb: '30px',
      }}
    >
      <CardContent style={{ padding: '20px' }}>
        <Typography fontWeight={500} fontSize="18px">
          {subject}
        </Typography>
      </CardContent>
    </Card>
  );
};

const dialogActionsStyles: SxProps = {
  backgroundColor: '#003768',
  color: '#ffffff',
  justifyContent: 'center',
  alignItems: 'center',
  height: '75px',
  p: 0,
};

const ViewScheduleModal: FunctionComponent<ViewScheduleModalProps> = ({
  open,
  onClose,
  sectionId,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const scheduleState = useAppSelector(getScheduleState);
  const authState = useAppSelector(getAuthState);

  // State
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [isRescheduleModalOpen, setRescheduleModalOpen] = useState(false);

  const authUser = authState.user;
  const scheduleEventData = scheduleState.userScheduleEvent;
  const classStatusChipData: ChipProps = {
    size: 'small',
    color: 'success',
    label: 'ACTIVE',
    sx: {
      borderRadius: '7px',
      fontSize: '14px',
      fontWeight: 600,
      width: 'fit-content',
      mb: '20px',
      textTransform: 'capitalize',
    },
  };

  let identifiedUserRole: TIdentifiedUserRole | undefined;
  let classTitle = '';
  let subject = '';
  let subjectColor = '';
  let subjectIconName = '';
  let classStart: Moment | undefined;
  let classEnd: Moment | undefined;
  let startDateText = '';
  let startAndEndTimeText = '';
  let durationText = '';
  let zoomRequest: IZoomMeetingRequest | undefined;
  let isCanceled = false;
  let isRequestPending = false;
  let isExpired = false;
  let classLink = '';
  let tutors: IScheduleFullEvent['tutors'] = [];
  let students: IScheduleFullEvent['students'] = [];

  if (scheduleEventData) {
    identifiedUserRole = scheduleEventData.identifiedUserRole;
    tutors = scheduleEventData.tutors || [];
    students = scheduleEventData.students || [];
    classTitle = scheduleEventData.title;
    subject = scheduleEventData.subject;
    subjectColor = getSubjectColorNew(subject);
    subjectIconName = getSubjectIconName(subject);
    zoomRequest = scheduleEventData.zoomRequest;
    classStart = moment(scheduleEventData.start);
    classEnd = moment(scheduleEventData.end);
    startDateText = classStart.format('dddd MMM DD YYYY');
    startAndEndTimeText = `${classStart.format('hh:mm A')} to ${classEnd.format(
      'hh:mm A'
    )}`;

    isExpired = moment() > classStart;
    const _hours = Math.floor(scheduleEventData.duration / 60);
    const _mins = scheduleEventData.duration % 60;

    if (_hours) {
      durationText = `${_hours} Hour${_hours > 1 ? 's' : ''}`;

      if (_mins) {
        durationText += ` ${_mins} Minute${_mins > 1 ? 's' : ''}`;
      }
    } else {
      durationText += ` ${_mins} Minute${_mins > 1 ? 's' : ''}`;
    }

    if (zoomRequest) {
      isCanceled =
        zoomRequest.type === ZOOM_MEETING_REQUEST_TYPES.CANCEL &&
        zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.APPROVED;

      classStatusChipData.label = `${zoomRequest.type} - ${zoomRequest.status}`;

      if (zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.APPROVED) {
        classStatusChipData.color = 'success';
      }

      if (zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.PENDING) {
        isRequestPending = true;
        classStatusChipData.color = 'warning';
      }

      if (zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.REJECTED) {
        classStatusChipData.color = 'error';
      }
    }

    if (
      !isCanceled &&
      !isRequestPending &&
      !isExpired &&
      scheduleEventData.waitingId
    ) {
      classLink = getClassShortUrlByWaitingId(scheduleEventData.waitingId);
    }
  }

  const isCancelAllowed =
    scheduleEventData &&
    scheduleEventData.type === SCHEDULE_TYPES.ONE_TO_ONE &&
    !isCanceled &&
    !isRequestPending &&
    !isExpired &&
    checkUserPermission(authUser, {
      schedule: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT],
    });

  const isRescheduleAllowed =
    scheduleEventData &&
    scheduleEventData.type === SCHEDULE_TYPES.ONE_TO_ONE &&
    !isCanceled &&
    !isRequestPending &&
    !isExpired &&
    checkUserPermission(authUser, {
      schedule: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT],
    });

  useEffect(() => {
    dispatch(getScheduleByIdAsUser({ sectionId }));

    return () => {
      dispatch(resetUserScheduleEvent());
    };
  }, []);

  const handleCancelSubmitSuccess = (section: ISectionForUser) => {
    dispatch(
      updateScheduleEvent({
        zoomRequest: section.zoomRequest,
      })
    );

    if (
      section.zoomRequest &&
      section.zoomRequest.type === ZOOM_MEETING_REQUEST_TYPES.CANCEL
    ) {
      if (
        section.zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.APPROVED
      ) {
        dispatch(removeUserScheduleEventFromList(section._id));
      } else if (
        section.zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.PENDING
      ) {
        dispatch(
          updateOneUserScheduleItem({
            id: section._id,
            attributes: {
              zoomRequest: section.zoomRequest,
            },
          })
        );
      }
    }

    setCancelModalOpen(false);
  };

  const handleRescheduleSubmitSuccess = (section: ISectionForUser) => {
    dispatch(
      updateScheduleEvent({
        start: section.dateTime,
        end: moment(section.dateTime)
          .add(section.duration, 'minutes')
          .toISOString(),
        title: section.title,
        duration: section.duration,
        status: section.status,
        zoomRequest: section.zoomRequest,
      })
    );

    dispatch(
      updateOneUserScheduleItem({
        id: section._id,
        attributes: {
          start: section.dateTime,
          end: moment(section.dateTime)
            .add(section.duration, 'minutes')
            .toISOString(),
          title: section.title,
          duration: section.duration,
          status: section.status,
          zoomRequest: section.zoomRequest,
        },
      })
    );

    setRescheduleModalOpen(false);
  };

  const renderTutors = () => {
    if (tutors.length && identifiedUserRole === IDENTIFIED_USER_ROLES.STUDENT) {
      return (
        <div>
          <Grid container alignItems="center" columnGap="10px">
            <Grid item>
              <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                Tutors :
              </Typography>
            </Grid>
            <Grid item xs>
              <Stack
                direction="row"
                alignItems="center"
                columnGap="10px"
                rowGap="10px"
                flexWrap="wrap"
              >
                {tutors.map((tutorItem) => {
                  const { tutor } = tutorItem;

                  const avatarUrl = tutor && tutor.avatar ? tutor.avatar : '';

                  return (
                    <ShortUserCard
                      short
                      key={`tutor-${tutorItem._id}`}
                      firstName={tutor.firstname}
                      lastName={tutor.lastname}
                      avatar={avatarUrl}
                    />
                  );
                })}
              </Stack>
            </Grid>
          </Grid>
        </div>
      );
    }

    return null;
  };

  const renderStudents = () => {
    if (students.length && identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR) {
      return (
        <div>
          <Grid container alignItems="center" columnGap="10px">
            <Grid item>
              <Typography fontSize="16px" fontWeight={500} color="#1E293B">
                Students :
              </Typography>
            </Grid>
            <Grid item xs>
              <Stack
                direction="row"
                alignItems="center"
                columnGap="10px"
                rowGap="10px"
                flexWrap="wrap"
              >
                {students.map((studentItem) => {
                  const { student } = studentItem;

                  const avatarUrl =
                    student && student.avatar ? student.avatar : '';

                  return (
                    <ShortUserCard
                      short
                      key={`student-${studentItem._id}`}
                      firstName={student.firstname}
                      lastName={student.lastname}
                      avatar={avatarUrl}
                    />
                  );
                })}
              </Stack>
            </Grid>
          </Grid>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '500px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: isXsScreen ? '15px' : '30px 30px 20px 30px',
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Class Details
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: isXsScreen ? '7px' : '22px',
          top: isXsScreen ? '7px' : '22px',
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: isXsScreen ? '0 15px' : '0 30px' }}>
        {scheduleState.status === 'loading' && !scheduleEventData ? (
          <Stack sx={{ p: '32px' }}>
            <BasicLoader />
          </Stack>
        ) : null}
        {scheduleEventData ? (
          <Stack>
            {!(classStatusChipData.label === 'ACTIVE' && isExpired) ? (
              <Chip {...classStatusChipData} />
            ) : null}
            <SubjectCard
              subject={subject}
              subjectColor={subjectColor}
              subjectIconName={subjectIconName}
            />
            <Stack p="0 15px" rowGap="20px">
              <div>
                <DetailItem icon={EventAvailableIcon} title={startDateText} />
              </div>
              <div>
                <DetailItem icon={AccessTimeIcon} title={startAndEndTimeText} />
              </div>
              <div>
                <DetailItem icon={TimelapseIcon} title={durationText} />
              </div>
              <div>
                <TextDetailItem title="Platform" description="Zoom" />
              </div>
              {renderTutors()}
              {renderStudents()}
              <div>
                <TextDetailItem title="Class Title" description={classTitle} />
              </div>
              {isCancelAllowed || isRescheduleAllowed ? (
                <Stack direction="row" alignItems="center" mt="20px" mb="20px">
                  {isCancelAllowed ? (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => setCancelModalOpen(true)}
                      sx={{
                        fontWeight: 600,
                        fontSize: isXsScreen ? '12px' : '14px',
                        textDecoration: 'underline',
                        colro: '#FF5630',
                      }}
                    >
                      Cancel Class ?
                    </Button>
                  ) : null}
                  {isRescheduleAllowed ? (
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setRescheduleModalOpen(true)}
                      sx={{
                        fontWeight: 600,
                        fontSize: isXsScreen ? '12px' : '14px',
                        textDecoration: 'underline',
                        color: '#1E293B',
                      }}
                    >
                      Reschedule Class ?
                    </Button>
                  ) : null}
                </Stack>
              ) : (
                <div style={{ minHeight: '20px' }} />
              )}
            </Stack>
          </Stack>
        ) : null}
      </DialogContent>
      {/* MODALS */}
      {classLink ? (
        <DialogActions sx={dialogActionsStyles}>
          <Typography
            component="a"
            href={classLink}
            target="_blank"
            color="#ffffff"
            fontSize="18px"
            fontWeight={600}
            sx={{ textDecoration: 'none', '&:hover': { opacity: 0.7 } }}
          >
            Join Class
          </Typography>
        </DialogActions>
      ) : null}
      {isCancelModalOpen && identifiedUserRole ? (
        <ClassCancellationModal
          sectionId={sectionId}
          open={isCancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          onSubmitted={handleCancelSubmitSuccess}
          identifiedUserRole={identifiedUserRole}
        />
      ) : null}
      {isRescheduleModalOpen && identifiedUserRole ? (
        <ClassRescheduleModal
          sectionId={sectionId}
          open={isRescheduleModalOpen}
          onClose={() => setRescheduleModalOpen(false)}
          onSubmitted={handleRescheduleSubmitSuccess}
          identifiedUserRole={identifiedUserRole}
        />
      ) : null}
    </Dialog>
  );
};

export default ViewScheduleModal;
